import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Button,
  Card,
  MenuItem,
  Select,
  TextField,
  FormControl,
  InputLabel,
  FormHelperText,
} from "@mui/material";
import MainContainer from "../../../layout/MainContainer";
import { Loader, showError, showToast } from "../../../constants";
import { GetCategories, AddCategory, UpdateCategory } from "../../../services/home";

interface Category {
  _id: string;
  name: string;
  description?: string;
  parentCategoryId?: string | null;
}

const CategoryForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isEdit = Boolean(id);
  const existingCategory = location.state?.item as Category | undefined;

  const [loading, setLoading] = useState(false);
  const [parentCategories, setParentCategories] = useState<Category[]>([]);

  const fetchParentCategories = async () => {
    try {
      const response = (await GetCategories({ parentCategoryId: "null" })) as {
        code: number;
        data: Category[];
      };
      if (response?.code === 200) {
        setParentCategories(response.data || []);
      }
    } catch (error) {
      showError("Failed to load parent categories");
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: existingCategory?.name ?? "",
      description: existingCategory?.description ?? "",
      parentCategoryId: existingCategory?.parentCategoryId ?? "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Name is required").max(100, "Max 100 characters"),
      description: Yup.string().required("Description is required").max(500, "Max 500 characters"),
      parentCategoryId: Yup.string().nullable(),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const payload = {
          name: values.name,
          description: values.description || undefined,
          parentCategoryId: values.parentCategoryId || null,
        };
        const response = (await (isEdit
          ? UpdateCategory(id!, payload)
          : AddCategory(payload))) as { code: number };
        if (response?.code === 200 || response?.code === 201) {
          showToast(`Category ${isEdit ? "updated" : "created"} successfully`);
          navigate("/manage-categories");
        }
      } catch (error) {
        showError("Something went wrong");
      } finally {
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    fetchParentCategories();
  }, []);

  return (
    <MainContainer>
      <Loader isLoad={loading} />
      <div className="main_loyout">
        <div className="dashboard">
          <h1 className="mn_hdng">{isEdit ? "Edit Category" : "Add Category"}</h1>
        </div>
        <Card className="cards" sx={{ padding: "24px" }}>
          <form onSubmit={formik.handleSubmit}>
            <TextField
              fullWidth
              label="Name"
              name="name"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
              sx={{ marginBottom: "20px" }}
            />

            <TextField
              fullWidth
              label="Description"
              name="description"
              value={formik.values.description}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.description && Boolean(formik.errors.description)}
              helperText={formik.touched.description && formik.errors.description}
              multiline
              rows={3}
              sx={{ marginBottom: "20px" }}
            />

            <FormControl fullWidth sx={{ marginBottom: "24px" }}>
              <InputLabel>Parent Category (leave blank to create a parent)</InputLabel>
              <Select
                name="parentCategoryId"
                value={formik.values.parentCategoryId ?? ""}
                label="Parent Category (leave blank to create a parent)"
                onChange={formik.handleChange}
              >
                <MenuItem value="">None (top-level category)</MenuItem>
                {parentCategories
                  .filter((cat) => !isEdit || cat._id !== id)
                  .map((cat) => (
                    <MenuItem key={cat._id} value={cat._id}>
                      {cat.name}
                    </MenuItem>
                  ))}
              </Select>
              {formik.touched.parentCategoryId && formik.errors.parentCategoryId && (
                <FormHelperText error>{formik.errors.parentCategoryId}</FormHelperText>
              )}
            </FormControl>

            <Button
              type="submit"
              className="btn btn_primary"
              disabled={formik.isSubmitting}
            >
              {isEdit ? "Update" : "Create"}
            </Button>
            <Button
              className="btn btn_secondary"
              onClick={() => navigate(-1)}
              sx={{ marginLeft: "12px" }}
            >
              Cancel
            </Button>
          </form>
        </Card>
      </div>
    </MainContainer>
  );
};

export default CategoryForm;
